// src/components/ReferralLinks.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Copy, Check, Share2 } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

export const ReferralLinks = () => {
  const { user } = useAuth();
  const [referralCode, setReferralCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [commissionRate, setCommissionRate] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Get user's referral info
  useEffect(() => {
    if (user) {
      fetchReferralInfo();
    }
  }, [user]);
  
  const fetchReferralInfo = async () => {
    if (!user || !user.email) return;
    
    try {
      setLoading(true);
      
      console.log('🔍 ReferralLinks: Fetching referral info for email:', user.email);
      
      // Get profile info by EMAIL instead of ID
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('referral_code, commission_rate')
        .eq('email', user.email.toLowerCase())
        .single();
        
      if (profileError) {
        console.error('❌ ReferralLinks: Profile query failed:', profileError);
      } else {
        console.log('✅ ReferralLinks: Profile data retrieved:', profile);
        
        if (profile?.referral_code) {
          setReferralCode(profile.referral_code);
          setCommissionRate(profile.commission_rate || '40%');
        }
      }
      
      // Get any referrals this user has (still use auth user.id for referrals table)
      const { data: referralList } = await supabase
        .from('referrals')
        .select('*')
        .eq('referrer_id', user.id);
        
      if (referralList) {
        setReferrals(referralList);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching referral info:', err);
      setLoading(false);
    }
  };
  
  const copyCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Card className="p-6 glass-card overflow-hidden">
      <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Your Affiliate Dashboard</h2>
      
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700 dark:border-primary-400"></div>
        </div>
      ) : (
        <>
          {/* Main discount code section */}
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 p-6 rounded-xl mb-6 border border-primary-200/50 dark:border-primary-700/50">
            <div className="flex items-center mb-3">
              <Share2 className="mr-2 text-primary-600 dark:text-primary-400" size={20} />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Your Discount Code</h3>
            </div>
            
            <div className="flex items-center justify-between mb-4">
              <code className="bg-white dark:bg-gray-800 px-4 py-3 rounded-lg text-xl font-mono font-bold text-primary-700 dark:text-primary-300 border border-gray-200 dark:border-gray-600">
                {referralCode || 'Contact support to get your code'}
              </code>
              {referralCode && (
                <Button 
                  onClick={copyCode}
                  className="ml-3 flex items-center bg-primary-700 hover:bg-primary-800 dark:bg-primary-600 dark:hover:bg-primary-700 text-white"
                >
                  {copied ? <Check size={16} className="mr-1" /> : <Copy size={16} className="mr-1" />}
                  {copied ? 'Copied!' : 'Copy Code'}
                </Button>
              )}
            </div>
            
            <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
              <p>💰 <strong>Share this code with your audience!</strong></p>
              <p>🎯 When they use code <strong>{referralCode}</strong> at checkout, they get <strong>20% off their plan</strong></p>
              <p>📊 This tracks your sale and gets you <strong>{commissionRate} commission</strong> on every conversion</p>
            </div>
          </div>

          {/* Why codes are better */}
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg mb-6 border border-green-200 dark:border-green-700">
            <h4 className="font-medium text-green-800 dark:text-green-300 mb-2">✨ Why Discount Codes Work Better</h4>
            <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
              <li>• <strong>100% Accurate Tracking</strong> - Every code usage is recorded at checkout</li>
              <li>• <strong>Customer Incentive</strong> - 20% discount motivates people to complete purchase</li>
              <li>• <strong>Easy to Share</strong> - Works in posts, videos, emails, anywhere</li>
              <li>• <strong>No Broken Links</strong> - Codes never expire or get lost</li>
            </ul>
          </div>

          {/* How to share section */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-6 border border-blue-200 dark:border-blue-700">
            <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">🚀 How to Share Your Code</h4>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li>• <strong>Social Media:</strong> "Get 20% off with code {referralCode}"</li>
              <li>• <strong>YouTube:</strong> Add code to video descriptions</li>
              <li>• <strong>Email Signature:</strong> Include in all your emails</li>
              <li>• <strong>Trading Communities:</strong> Share with fellow traders</li>
              <li>• <strong>Direct Messages:</strong> Send code to interested friends</li>
            </ul>
          </div>

          {/* Commission details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Your Commission Rate</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{commissionRate}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">per successful referral</p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Customer Discount</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">20% OFF</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">when they use your code</p>
            </div>
          </div>
          
          {/* Referral stats */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Your Referral History</h3>
            
            {referrals.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/50">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Customer Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Your Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((referral) => (
                      <tr key={referral.id} className="border-t border-gray-200 dark:border-gray-600">
                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{referral.referred_email || '—'}</td>
                        <td className="px-4 py-2 text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            referral.status === 'completed' 
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                              : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                          }`}>
                            {referral.status === 'completed' ? 'Paid' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                          {new Date(referral.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {referral.status === 'completed' 
                            ? `$${referral.commission_amount?.toFixed(2) || '0.00'}` 
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <p className="mb-2">No referrals yet!</p>
                <p className="text-sm">Start sharing your code <strong>{referralCode}</strong> to earn commissions.</p>
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
};